import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  StackProps,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { FileError, FileRejection, useDropzone } from "react-dropzone";
import {
  MdCheckCircle,
  MdOutlineDeleteOutline,
  MdOutlineError,
  MdOutlineFileUpload,
  MdOutlineUploadFile,
  MdClose,
} from "react-icons/md";
import { Except } from "type-fest";
import { focusRing } from "../../theme/focusStyles";
import { TooltipWithTouch } from "../TooltipWithTouch";

type FileDropZoneProps = {
  value: File[];
  onChange: (files: File[]) => void;
  id?: string;
} & Except<StackProps, "onChange">;

/**
 * File Drop Zone input component.
 *
 * Maintains a similar interface to other Chakra input components.
 */
export function FileDropZoneInput({
  value,
  onChange,
  id,
  ...props
}: FileDropZoneProps): React.ReactElement {
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);

  const [isRecentDrop, setIsRecentDrop] = useState<boolean>(false);

  const recentDropTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (recentDropTimeoutRef.current) {
        clearTimeout(recentDropTimeoutRef.current);
      }
    };
  }, []);

  const removeFile = useCallback(
    (index: number) => {
      onChange(value.filter((_, currentIndex) => currentIndex !== index));
    },
    [value, onChange],
  );

  const duplicateFileValidator = useCallback(
    (file: File) => {
      if (value.some((f) => f.name === file.name)) {
        return {
          code: "duplicate-file-name",
          message: `This file has already been added.`,
        };
      }
      return null;
    },
    [value],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length === 0 && rejectedFiles.length === 0) {
        return;
      }

      if (acceptedFiles.length > 0) {
        onChange([...value, ...acceptedFiles]);
      }

      if (rejectedFiles.length > 0) {
        setRejectedFiles(rejectedFiles);
      } else {
        setRejectedFiles([]);
      }

      setIsRecentDrop(true);

      if (recentDropTimeoutRef.current) {
        clearTimeout(recentDropTimeoutRef.current);
      }

      recentDropTimeoutRef.current = setTimeout(() => {
        setIsRecentDrop(false);
        recentDropTimeoutRef.current = undefined;
      }, 1000);
    },
    [value, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: [".csv", ".xlsx", ".txt"],
    validator: duplicateFileValidator,
    onDrop: handleDrop,
  });

  const fileResults = useMemo<FileResult[]>(() => {
    const storedResults: FileResult[] = value.map((file, index) => ({
      accepted: true,
      file,
      storedIndex: index,
    }));

    const rejectedResults: FileResult[] = rejectedFiles.map(
      (rejectedFile, index) => ({
        accepted: false,
        file: rejectedFile.file,
        errors: rejectedFile.errors,
        rejectedIndex: index,
      }),
    );

    return [...rejectedResults, ...storedResults.reverse()];
  }, [value, rejectedFiles]);

  const closeRejectedFile = useCallback((index: number) => {
    setRejectedFiles((files) =>
      files.filter((_, currentIndex) => currentIndex !== index),
    );
  }, []);

  return (
    <VStack w="full" align="flex-start" spacing={4} {...props}>
      <Flex
        w="full"
        position="relative"
        direction="column"
        alignItems="center"
        justifyContent="center"
        px={2}
        py={4}
        borderWidth="2px"
        borderRadius="md"
        borderColor={isDragActive ? "gray.700" : "gray.400"}
        backgroundColor="gray.25"
        boxShadow={isDragActive ? "md" : "none"}
        borderStyle="dashed"
        transition="border 0.24s ease-in-out"
        cursor="pointer"
        fontSize="md"
        gap={2}
        _focusVisible={focusRing}
        {...getRootProps()}
      >
        <input {...getInputProps({ id })} />
        <Flex
          position="absolute"
          inset={0}
          backgroundColor="teal.50"
          opacity={isDragActive || isRecentDrop ? 1 : 0}
          transition="opacity 0.15s ease-in-out"
          align="center"
          justify="center"
          textAlign="center"
          zIndex={1}
          pointerEvents="none"
        >
          <Text
            fontWeight="bold"
            color={isRecentDrop ? "green.500" : "teal.500"}
            fontSize="md"
          >
            {isRecentDrop ? "File Added" : "Drop here"}
          </Text>
        </Flex>
        <Icon as={MdOutlineFileUpload} boxSize={10} color="gray.700" />
        <VStack spacing={1}>
          <Text fontSize="md">Drag and drop files here</Text>
          <HStack spacing={1}>
            <Text fontSize="md">or </Text>
            <Text
              fontSize="md"
              fontWeight="semibold"
              pointerEvents="auto"
              color="primary.500"
            >
              click to browse
            </Text>
          </HStack>
        </VStack>
        <Text fontSize="sm">Accepts .csv or .xlsx</Text>
      </Flex>

      {fileResults.length !== 0 && (
        <VStack
          w="full"
          spacing={2}
          sx={{
            "@keyframes fadeSlideIn": {
              from: { opacity: 0, transform: "translateY(-6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {fileResults.map((result) => (
            <VStack
              w="full"
              key={result.accepted ? result.storedIndex : result.rejectedIndex}
              borderWidth={result.accepted ? "1px" : undefined}
              borderColor={result.accepted ? "gray.300" : "red.500"}
              borderRadius="md"
              backgroundColor={result.accepted ? undefined : "red.50"}
              py={2}
              px={3}
              spacing={2}
              boxShadow="sm"
              animation={`fadeSlideIn 0.25s ease-out`}
            >
              <HStack w="full" alignItems="center">
                <Box position="relative" display="inline-flex">
                  <Icon as={MdOutlineUploadFile} color="gray.600" boxSize={7} />
                  <Icon
                    as={result.accepted ? MdCheckCircle : MdOutlineError}
                    borderRadius="full"
                    backgroundColor="white"
                    color={result.accepted ? "green.500" : "red.500"}
                    boxSize={3}
                    position="absolute"
                    bottom={0}
                    right={-1}
                  />
                </Box>

                <VStack flex="1 1 0" spacing={1} align="flex-start">
                  <TooltipWithTouch label={result.file.name}>
                    <Text
                      width="full"
                      fontSize="sm"
                      noOfLines={1}
                      wordBreak="break-all"
                    >
                      {result.file.name}
                    </Text>
                  </TooltipWithTouch>
                  <Text fontSize="xs" color="gray.600">
                    {formatFileSize(result.file.size)}
                  </Text>
                </VStack>
                {!result.accepted && (
                  <>
                    <Tag borderRadius="full" colorScheme="red" flexShrink={0}>
                      Failed
                    </Tag>
                    <IconButton
                      aria-label="Close file error"
                      size="sm"
                      fontSize="xl"
                      variant="ghost"
                      colorScheme="red"
                      icon={<Icon as={MdClose} />}
                      onClick={() => closeRejectedFile(result.rejectedIndex)}
                    />
                  </>
                )}
                {result.accepted && (
                  <IconButton
                    aria-label="Remove file"
                    size="sm"
                    fontSize="xl"
                    variant="ghost"
                    colorScheme="red"
                    icon={<Icon as={MdOutlineDeleteOutline} />}
                    onClick={() => removeFile(result.storedIndex)}
                  />
                )}
              </HStack>

              {!result.accepted && (
                <VStack w="full" align="flex-start" fontSize="sm">
                  {result.errors.map((error) => (
                    <Text key={error.code} color="gray.600">
                      {error.message}
                    </Text>
                  ))}
                </VStack>
              )}
            </VStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
}

type FileResult =
  | { accepted: true; file: File; storedIndex: number }
  | { accepted: false; file: File; errors: FileError[]; rejectedIndex: number };
